import { PlusOutlined } from '@ant-design/icons'
import { type Home, UserRole } from '@lmc/ts-common'
import {
  Button,
  EmptyState,
  PageHeader,
  PermissionIcon,
  Remove,
  Search
} from '@lmc/ui-kit'
import { Form, Modal } from 'antd'
import { showToast } from 'common/utils'
import { useRoleAccess } from 'components/AccessControl'
import { isNoticeboardGranted } from 'components/navigation/utils'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { NAMESPACE } from 'locale'
import { generalConstants } from 'locale/general'
import { noticeboardConstants } from 'locale/noticeboard'
import { debounce } from 'lodash'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import NoticeForm from './components/NoticeForm'
import NoticesTable, {
  type NoticeboardSortObject,
  noticeboardDefaultSort
} from './components/NoticesTable'
import useNotices, {
  NOTICES_FETCH_TYPE,
  type UseNoticesReturnMany
} from './hooks/useNotices'

const Noticeboard = () => {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [sortParams, setSortParams] = useState<NoticeboardSortObject>(
    noticeboardDefaultSort
  )
  const [form] = Form.useForm()
  const {
    isLoading,
    count,
    result: notices,
    createNotice
  } = useNotices({
    title: search,
    fetch: NOTICES_FETCH_TYPE.LIST,
    page,
    sortParams
  }) as UseNoticesReturnMany
  const { t } = useTranslation(NAMESPACE.NOTICEBOARD)
  const { t: generalTranslations } = useTranslation(NAMESPACE.GENERAL)
  const features = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  dayjs.extend(relativeTime)

  const hasRolePermission = useRoleAccess(
    [UserRole.careHomeAdmin, UserRole.seniorCarer],
    [
      {
        i: 'create',
        this: 'Notice'
      }
    ]
  )

  const debouncedSetSearch = debounce((value: string) => {
    setSearch(value)
  }, 500)

  const isNoticeboardEnabled = isNoticeboardGranted(features)

  if (!isNoticeboardEnabled) {
    return (
      <Wrapper>
        <EmptyState
          icon={<PermissionIcon />}
          title={t(noticeboardConstants.NOT_ALLOWED_TITLE)}
          description={t(noticeboardConstants.NOT_ALLOWED_DESCRIPTION)}
        />
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      <PageHeader
        title={<TitleContainer>{t(noticeboardConstants.TITLE)}</TitleContainer>}
        backButtonHref="/"
      >
        <ActionsWrapper>
          <SearchWrapper>
            <Search
              placeholder={t(generalConstants.TYPE_TO_SEARCH, {
                ns: NAMESPACE.GENERAL
              })}
              onChange={(event) =>
                debouncedSetSearch(event.target.value.trim())
              }
            />
          </SearchWrapper>
          <CreateButton
            variant="primary"
            icon={<PlusOutlined />}
            onClick={() => setShowCreateModal(true)}
            data-cy="create-notice-btn"
            disabledByPermission={!hasRolePermission}
          >
            {t(noticeboardConstants.CREATE)}
          </CreateButton>
        </ActionsWrapper>
      </PageHeader>
      <NoticesTable
        isLoading={isLoading}
        onSortChange={(sort) => setSortParams(sort)}
        count={count || 0}
        page={page}
        setPage={setPage}
        notices={notices || []}
      />
      <CustomModal
        open={showCreateModal}
        closeIcon={<Remove />}
        width="100%"
        footer={
          <ButtonsWrapper>
            <Button onClick={() => setShowCreateModal(false)}>
              {generalTranslations(generalConstants.CANCEL)}
            </Button>
            <Button variant="primary" onClick={form.submit}>
              {t(noticeboardConstants.POST)}
            </Button>
          </ButtonsWrapper>
        }
        onCancel={() => setShowCreateModal(false)}
        onOk={form.submit}
      >
        <StyledTitle>{t(noticeboardConstants.CREATE)}</StyledTitle>
        <NoticeForm
          form={form}
          onFinish={(values) => {
            createNotice(values)
            setShowCreateModal(false)
            showToast({ title: t(noticeboardConstants.TOAST_CREATE) })
          }}
        />
      </CustomModal>
    </Wrapper>
  )
}

const CustomModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 16px !important;
  }
`

const Wrapper = styled.div`
  max-width: 1080px;
  padding-top: 24px;
  padding-bottom: 50px;
  margin-left: auto;
  margin-right: auto;

  ${({ theme }) => theme.mediaQueries.tabletDown} {
    padding: 24px;
  }
`
const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const SearchWrapper = styled.div`
  width: 232px;
`

const ActionsWrapper = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    justify-content: center;
  }
`

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const CreateButton = styled(Button)`
  white-space: nowrap;
`

const StyledTitle = styled.div`
  font-size: 24px;
  margin-bottom: 24px;
  font-weight: 500;
  line-height: 24px;
  // needed to overwrite default styling
  text-transform: none !important;
  color: ${({ theme }) => theme.color.neutral.color3};
`

export default Noticeboard
